/* eslint camelcase: "off" */
import { Loader, NavUpLinkBar, Summary } from "uikit"
import api from "api"
import AccountConfirmSelectContainer from "components/accounts/AccountConfirmSelector"
import AccountDetailsView from "components/accounts/AccountDetailsView"
import { AccountsAssetsTable } from "components/accounts/AccountsAssetsTable"
import { AccountStatistics } from "components/accounts/AccountStatistics"
import AccountTypeSelector from "components/accounts/AccountTypeSelector"
import { SurveyPoll } from "components/accounts/SurveyPoll"
import AccountsConnector from "components/accounts/AccountsConnector"
import PropertyDetailsView from "components/properties/PropertyDetailsView"
import PropertySelector from "components/properties/PropertyTypSelector"
import React, { useState, useCallback } from "react"
import { Redirect, Route, Switch, withRouter } from "react-router-dom"
import { useStore } from "store"
import "./AccountsAndProperty.css"
import { EmptyStateView } from "./EmptyState"

const AccountsAndPropertySummary = ({
  updateHouseholdState,
  household,
  updateHouseholdConnectionsRefreshStatus,
  refreshHouseholdConnections,
  people,
  baseUrl,
  householdState,
  updateHousehold,
  history
}) => {
  const [showAccountsConnector, setShowAccountsConnector] = useState(
    window &&
      window.location &&
      window.location.search === "?add-linked-accounts"
  )
  const [waitingForRefresh, setWaitingForRefresh] = useState(false)
  const householdId = household.id
  const [accountsObject, { monitorConnectionRefresh }] = useStore(
    "householdAccounts",
    householdId
  )
  const accounts = Object.values(accountsObject)
  const [connections] = useStore("householdConnections", householdId)
  const [propertiesObject] = useStore("householdProperties", householdId)
  const [subscription] = useStore("subscriptions", householdId)
  const showOffers =
    subscription &&
    subscription.features &&
    subscription.features.unsolicited_offers
  const properties = Object.values(propertiesObject)

  const refreshConnections = useCallback(async () => {
    await updateHouseholdState({
      id: householdId,
      isRefreshingAccounts: true,
      refreshingConnectingIds: accounts
        .filter(({ connection_id }) => !!connection_id)
        .map(({ connection_id }) => connection_id)
    })
    setWaitingForRefresh(true)

    try {
      await refreshHouseholdConnections(householdId)
      setWaitingForRefresh(false)
    } catch (error) {
      setWaitingForRefresh(false)
    }
  }, [householdId, accounts, updateHouseholdState, refreshHouseholdConnections])

  if (
    accounts.length === 0 &&
    properties.length === 0 &&
    !householdState["isRefreshingAccounts"]
  ) {
    return <Redirect to={`/${baseUrl.split("/")[1]}/introduction`} />
  }

  if (window) {
    window.trackEvent("view-accounts+property")
  }

  return (
    <Summary>
      <Summary.Header
        heading="Accounts + Property"
        description={() => (
          <div>
            Collect all your accounts to see your complete financial picture.
            Securely link your accounts to easily gather the details and keep
            them current.
          </div>
        )}
      >
        <AccountStatistics accounts={accounts} properties={properties} />
      </Summary.Header>
      <Summary.Body className="AccountSummary__SummaryBody">
        {waitingForRefresh ? (
          <Loader text="Refreshing accounts..." active={waitingForRefresh} />
        ) : null}
        <AccountsAssetsTable
          householdState={householdState}
          people={people}
          accounts={accounts}
          connections={Object.values(connections)}
          properties={properties}
          baseUrl={baseUrl}
          household={household}
          showAccountsConnector={() => setShowAccountsConnector(true)}
          refreshConnections={refreshConnections}
          updateHouseholdConnectionsRefreshStatus={
            updateHouseholdConnectionsRefreshStatus
          }
          showOffers={showOffers}
        />
        {showAccountsConnector ? (
          <AccountsConnector
            getFastLink={messageId =>
              `${api.baseURL}households/${householdId}/connections/linker?message_id=${messageId}`
            }
            household={household}
            onSuccess={async () => {
              await updateHouseholdState({
                id: householdId,
                isRefreshingAccounts: true,
                refreshingConnectingIds: []
              })
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              )
              await monitorConnectionRefresh(householdId)
              setShowAccountsConnector(false)
            }}
            onFailure={() => {
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              )
              setShowAccountsConnector(false)
            }}
          />
        ) : null}
      </Summary.Body>
      <Summary.Footer
        nextStep={
          accounts.every(({ is_confirmed }) => is_confirmed)
            ? "Organize Your Income and Benefits"
            : "Please Confirm Details Before Continuing"
        }
        submitButtonLabel="Continue"
        disableSubmitButton={
          !accounts.every(({ is_confirmed }) => is_confirmed)
        }
        onSubmit={async () => {
          await updateHousehold({
            id: householdId,
            has_confirmed_accounts: true,
            has_confirmed_properties: true
          })
          history.push(
            baseUrl
              .split("/")
              .slice(0, -1)
              .join("/") + "/income+benefits"
          )
        }}
      />
    </Summary>
  )
}

export const AccountsAndProperty = withRouter(
  ({ people, household, user, match: { url: baseUrl }, history }) => {
    const {
      updateHouseholdState,
      updateHousehold,
      addAccount,
      updateAccount,
      deleteAccount,
      addProperty,
      updateProperty,
      deleteProperty,
      updateHouseholdConnectionsRefreshStatus,
      refreshHouseholdConnections
    } = useStore()
    const [householdState] = useStore("householdState", household.id)
    const [accountIds] = useStore("householdAccountIds", household.id)
    const [accounts] = useStore("accounts")
    const householdAccounts = accountIds.map(id => accounts[id])
    return (
      <Switch>
        <Route
          path={`${baseUrl}/getting-started`}
          exact
          render={() => (
            <EmptyStateView
              baseUrl={baseUrl}
              household={household}
              user={user}
            />
          )}
        />
        <Route
          path={baseUrl}
          exact
          render={() => (
            <AccountsAndPropertySummary
              {...{
                household,
                people,
                baseUrl,
                householdState,
                updateHouseholdState,
                updateHousehold,
                history,
                refreshHouseholdConnections,
                updateHouseholdConnectionsRefreshStatus
              }}
            />
          )}
        />
        <Route
          path={`${baseUrl}/survey`}
          exact
          render={() => (
            <SurveyPoll
              {...{ household, people, baseUrl }}
              continueUrl="/organize/accounts+property/getting-started"
            />
          )}
        />
        <Route
          path={`${baseUrl}/accounts/new`}
          exact
          render={() => (
            <AccountTypeSelectContainer
              baseUrl={baseUrl}
              onCancel={history.goBack}
            />
          )}
        />
        <Route
          path={`${baseUrl}/accounts/confirm/:id/:type`}
          exact
          render={({
            match: {
              params: { id, type }
            }
          }) => (
            <AccountConfirmSelectContainer
              accountId={id}
              type={type}
              baseUrl={baseUrl}
              onCancel={history.goBack}
            >
              Confirm account{" "}
            </AccountConfirmSelectContainer>
          )}
        />
        <Route
          path={`${baseUrl}/properties/new`}
          exact
          render={() => (
            <PropertyTypeSelectContainer
              baseUrl={baseUrl}
              onCancel={history.goBack}
            />
          )}
        />
        <Route
          path={`${baseUrl}/accounts/:id/:type/:confirm?`}
          exact
          render={({
            match: {
              params: { id, type, confirm }
            },
            location: { state }
          }) => {
            const successUrl =
              state && state.success ? `${state.success}${type}_plan` : baseUrl
            return (
              <div>
                <NavUpLinkBar path={baseUrl} label="Accounts + Property" />
                <AccountDetailsView
                  accountId={id}
                  type={type}
                  confirm={confirm}
                  household={household}
                  isNew={id === "new"}
                  changeTypeLink={`${baseUrl}/accounts/confirm/${id}/${type}`}
                  success={<Redirect to={successUrl} />}
                  onSubmit={async values => {
                    if (id === "new") {
                      return await addAccount({
                        household_id: household.id,
                        type,
                        ...values
                      })
                    } else if (confirm) {
                      Object.assign(values, { is_confirmed: true })
                      return await updateAccount({
                        id,
                        type,
                        ...values
                      })
                    } else {
                      return await updateAccount({
                        id,
                        type,
                        ...values
                      })
                    }
                  }}
                  onDelete={async () => {
                    await deleteAccount({
                      household_id: household.id,
                      id
                    })
                  }}
                />
              </div>
            )
          }}
        />
        <Route
          path={`${baseUrl}/properties/:id/:type`}
          exact
          render={({
            match: {
              params: { id, type }
            }
          }) => (
            <div>
              <NavUpLinkBar path={baseUrl} label="Accounts + Property" />
              <PropertyDetailsView
                propertyId={id}
                household={household}
                type={type}
                isNew={id === "new"}
                success={<Redirect to={baseUrl} />}
                onSubmit={async values => {
                  if (id === "new") {
                    return await addProperty(
                      Object.assign(values, {
                        household_id: household.id,
                        type
                      })
                    )
                  }
                  return await updateProperty({ id, ...values })
                }}
                onDelete={async () => {
                  const loanType = type === "realestate" ? "frm" : "autoloan"
                  const loanAccounts = householdAccounts
                    .filter(({ type }) => type === loanType)
                    .filter(account => account.property_id === id)
                    .map(({ id }) => id)
                  await Promise.all(
                    loanAccounts.map(id =>
                      deleteAccount({
                        household_id: household.id,
                        id
                      })
                    )
                  )
                  await deleteProperty({
                    household_id: household.id,
                    id
                  })
                }}
              />
            </div>
          )}
        />
        <Redirect to={`${baseUrl}`} />
      </Switch>
    )
  }
)
class AccountTypeSelectContainer extends React.Component {
  state = { accountType: "" }
  render = () => {
    return (
      <AccountTypeSelector
        submit={({ accountType }) => this.setState({ accountType })}
        success={
          <Redirect
            to={`${this.props.baseUrl}/accounts/new/${this.state.accountType}`}
            push
          />
        }
        onCancel={() => {
          this.props.onCancel()
        }}
      />
    )
  }
}
class PropertyTypeSelectContainer extends React.Component {
  state = { propertyType: "" }
  render = () => {
    return (
      <PropertySelector
        submit={({ propertyType }) => this.setState({ propertyType })}
        onCancel={() => {
          this.props.onCancel()
        }}
        success={
          <Redirect
            to={`${this.props.baseUrl}/properties/new/${this.state.propertyType}`}
            push
          />
        }
      />
    )
  }
}
